/**
 * PillarTooltip — every plotted series at the hovered period, in the pillar's own order.
 *
 * The comparison IS the point of a grouped plot, so the tooltip reads all series at once
 * rather than whichever line the cursor happened to land on. Series that are off the plot
 * are absent here too — the legend says why they are missing; repeating it on every hover
 * would bury the numbers. A series with no value that month reads its honest em-dash
 * rather than being dropped, so a gap is legible as a gap.
 *
 * The colour swatch carries identity; the text stays in text tokens (never the series hue).
 */

import React from 'react'
import { EM_DASH } from './cellFormat'
import { unitLabel, type PillarPlotModel } from './pillarSeries'
import { isForecastAt } from './forecastSplit'

interface PillarTooltipProps {
  model: PillarPlotModel
  palette: readonly string[]
  active?: boolean
  payload?: Array<{ payload: Record<string, string | number | null> }>
}

function reading(
  value: string | number | null | undefined,
  model: PillarPlotModel,
  format: string
): string {
  if (value === null || value === undefined || typeof value !== 'number') return EM_DASH
  if (model.mode === 'pct_of_target') return `${value}% of target`
  if (format === 'gbp_k') return `£${value.toLocaleString()}k`
  if (format === 'pct') return `${value}%`
  return `${value.toLocaleString()} ${unitLabel(format as never)}`
}

export default function PillarTooltip({
  model,
  palette,
  active,
  payload,
}: PillarTooltipProps): React.JSX.Element | null {
  if (!active || !payload || payload.length === 0) return null
  const row = payload[0].payload
  const plotted = model.series.filter((series) => series.plotted)
  return (
    <div className="rounded-md border border-slate-200 bg-white px-2.5 py-2 text-xs shadow-md dark:border-slate-700 dark:bg-slate-900">
      <p className="mb-1 font-semibold text-slate-700 dark:text-slate-200">{String(row.label ?? '')}</p>
      {plotted.map((series) => (
        <p key={series.metricId} className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
          <span
            aria-hidden
            className="inline-block h-2 w-2 flex-shrink-0 rounded-full"
            style={{ background: palette[series.colorIndex % palette.length] }}
          />
          <span className="text-slate-600 dark:text-slate-300">{series.title}</span>
          <span className="ml-auto pl-3 tabular-nums">
            {reading(row[series.metricId], model, series.format)}
            {/* The dash says it on the chart; the tooltip has to say it in words, because a
                number read on hover is the one a reader writes down. */}
            {isForecastAt(row, series.metricId) && (
              <span className="ml-1 text-slate-400 dark:text-slate-500">forecast</span>
            )}
          </span>
        </p>
      ))}
    </div>
  )
}
